import * as React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import { Translate } from 'react-localize-redux';
import classnames from 'classnames';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import Editors from '@youwell/ckeditor5-builds-youwell';
import { connect } from 'react-redux';

import { Typography, Input } from '@material-ui/core';

import TextEditorUploadAdapterPlugin from './TextEditorUploadAdapterPlugin';
import { isIe, isBrowser } from '../../youwell-common/browserUtils';
import { htmlContentClassName } from '../../youwell-common/components/HtmlContent';

const editorTextColors = [
    {
        color: 'hsl(0, 0%, 0%)',
        label: 'Black',
    },
    {
        color: 'hsl(0, 0%, 30%)',
        label: 'Dim grey',
    },
    {
        color: 'hsl(0, 0%, 60%)',
        label: 'Grey',
    },
    {
        color: 'hsl(0, 0%, 90%)',
        label: 'Light grey',
    },
    {
        color: 'hsl(0, 0%, 100%)',
        label: 'White',
        hasBorder: true,
    },
    {
        color: '#B91400', // red
    },
    {
        color: 'hsl(0, 75%, 60%)',
        label: 'Red',
    },
    {
        color: 'hsl(30, 75%, 60%)',
        label: 'Orange',
    },
    {
        color: 'hsl(60, 75%, 60%)',
        label: 'Yellow',
    },
    // {
    //     color: palette.yellow,
    // },
    {
        color: 'hsl(90, 75%, 60%)',
        label: 'Light green',
    },
    {
        color: 'hsl(120, 75%, 60%)',
        label: 'Green',
    },
    {
        color: 'hsl(150, 75%, 60%)',
        label: 'Aquamarine',
    },
    {
        color: 'hsl(180, 75%, 60%)',
        label: 'Turquoise',
    },
    {
        color: '#007C91', // green
    },
    // {
    //     color: palette.blue,
    // },
    {
        color: 'hsl(210, 75%, 60%)',
        label: 'Light blue',
    },
    {
        color: 'hsl(240, 75%, 60%)',
        label: 'Blue',
    },
    {
        color: 'hsl(270, 75%, 60%)',
        label: 'Purple',
    },
    {
        color: '#000179',
    },
];

const styles = theme => ({
    root: {
        border: theme.youwell.borders.dim,

        '& a': {
            color: theme.youwell.colors.hightScore,
            textDecoration: 'underline',
            cursor: 'text',
        },

        '& .ck-content .image': {
            clear: 'none', // override ck-content to allow two images beside each other..
        },

    },
    toolbar: {
        padding: theme.spacing(1),
    },
    htmlEditor: {
        padding: theme.spacing(1),
    },
});


const ErrorFallback = ({
    classes, className, value, onChange, disabled,
}) => (
    <div className={classnames(classes.root, className)}>
        <div className={classes.htmlEditor}>
            <Input
                value={value}
                onChange={onChange}
                multiline
                minRows={5}
                disabled={disabled}
                fullWidth
            />
        </div>
    </div>
);

ErrorFallback.propTypes = {
    classes: PropTypes.object.isRequired,
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    className: PropTypes.string,
};

ErrorFallback.defaultProps = {
    disabled: false,
    className: null,
};

class TextEditor extends React.Component {
    state = {
        error: false,
    };

    linkAdapterRef = React.createRef();

    componentDidCatch = () => {
        this.setState({ error: true });
    };

    handleEditorChange = (event, editor) => {
        const data = editor.getData();
        this.props.onChange({ target: { value: data } });
    };

    render() {
        const {
            classes, disabled, value, className, onChange, allowUpload, appId,
        } = this.props;

        if (this.state.error || isIe) {
            return (<ErrorFallback classes={classes} disabled={disabled} value={value} className={className} onChange={onChange} />);
        }

        if (!isBrowser) {
            return (
                <div className={classnames(classes.root, className)} />
            );
        }

        const config = {
            toolbar: ['bold', 'italic', 'underline', 'fontColor',
                '|', 'bulletedList', 'numberedList', 'insertTable'],
            fontColor: {
                colors: editorTextColors,
            },
        };

        if (allowUpload) {
            config.toolbar.push('imageUpload');
            config.extraPlugins = [TextEditorUploadAdapterPlugin(appId)]
        }

        return (
            <div className={classnames(classes.root, className, htmlContentClassName)}>
                <CKEditor
                    editor={Editors.ClassicEditor}
                    data={value}
                    onChange={this.handleEditorChange}
                    disabled={disabled}
                    config={config}
                />
            </div>
        );
    }
}

TextEditor.propTypes = {
    classes: PropTypes.object.isRequired,
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    className: PropTypes.string,
    allowUpload: PropTypes.bool,
    appId: PropTypes.string.isRequired,
};

TextEditor.defaultProps = {
    disabled: false,
    className: null,
    allowUpload: false,
};

const mapStateToProps = state => ({
    appId: state.pageSettings.appConfig.appId,
});

const EditorComponent = connect(mapStateToProps, null)(withStyles(styles)(TextEditor));

class EditorErrorBoundary extends React.Component {
    static propTypes = {
        classes: PropTypes.object.isRequired,
        children: PropTypes.oneOfType([
            PropTypes.arrayOf(PropTypes.node),
            PropTypes.node,
        ]),
        className: PropTypes.string,
    };

    static defaultProps = {
        children: null,
        className: null,
    };

    state = {
        hasError: false,
    };

    componentDidCatch = (error, info) => {
        this.setState({ hasError: true });
        // eslint-disable-next-line no-console
        console.warn('Editor-error', { error, info });
    };

    render() {
        const { classes, className } = this.props;

        if (this.state.hasError) {
            return (
                <div className={classnames(classes.root, className)}>
                    <div className={classes.toolbar}>
                        <Typography color="error">
                            <Translate id="textEditor.errorOccuredLoadingEditor" />
                        </Typography>
                    </div>
                </div>
            );
        }
        return this.props.children;
    }
}

const ErrorBoundaryComponent = withStyles(styles)(EditorErrorBoundary);

export default React.forwardRef((props, ref) => (
    <ErrorBoundaryComponent>
        <EditorComponent {...props} forwardedRef={ref} />
    </ErrorBoundaryComponent>
));
